import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Box,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { postItem, putItem } from '../../../support/queries';
import { CacheKey } from '../../../types/cache-keys';

export type EscalationType = {
  id?: number;
  name: string;
  description?: string | null;
};

interface Props {
  open: boolean;
  editItem: EscalationType | null;
  onClose: () => void;
  readOnly?: boolean;
}

const EscalationTypeEditDialog: React.FC<Props> = ({
                                                     open,
                                                     editItem,
                                                     onClose,
                                                     readOnly = false,
                                                   }) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(editItem?.name ?? '');
    setDescription(editItem?.description ?? '');
  }, [editItem, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        id: editItem?.id,
        name,
        description,
      };

      if (editItem?.id) {
        await putItem('db/escalation-type', payload);
      } else {
        await postItem('db/escalation-type', payload);
      }

      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'escalationTypes'] });
      onClose();
    } catch (e) {
      alert('Failed to save escalation type.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editItem?.id ? 'Edit Escalation Type' : 'Add Escalation Type'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          margin="normal"
          disabled={readOnly || saving}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          margin="normal"
          disabled={readOnly || saving}
        />
        {saving && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!readOnly && (
          <Button onClick={handleSave} variant="contained" disabled={saving || !name.trim()}>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EscalationTypeEditDialog;